import React, { useEffect, useState } from "react";
import { Form, Table, Input, Button, Col, Row, Spin } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { ReactComponent as BackIcon } from "../../assets/back-icon.svg";
import { useDispatch, useSelector } from "react-redux";
import { fetchCallHistory } from "./redux/actions";
import moment from "moment";
import _ from "lodash";
import CustomPagination from "../../components/customPagination/CustomPagination";

const UserCallHistory = () => {
  const navigate = useNavigate();
  const { user_id } = useParams();
  const dispatch = useDispatch();

  const { callHistories, fetchCallHistoriesLoading } = useSelector(
    (state) => state.userManagerReducer
  );

  const [sortedInfo, setSortedInfo] = useState(null);
  const [dataSource, setDataSource] = useState([]);
  const [page, setPage] = useState(1);
  const [resultPerPage, setResultPerPage] = useState(10);
  const [search, setSearch] = useState({});

  const [debouncedCallApi] = useState(() => _.debounce(() => filterData(), 500));

  useEffect(() => {
    if (user_id) {
      dispatch(fetchCallHistory({ user_id }));
    }
  }, [dispatch, user_id]);

  useEffect(() => {
    filterData();
  }, [callHistories, search, sortedInfo]);

  const handleChange = (_, __, sorter) => {
    setSortedInfo(sorter);
  };

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return "-";
    const totalSeconds = Math.floor(seconds);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const handleSearch = (key, value) => {
    const updatedSearch = { ...search, [key]: value };
    setSearch(updatedSearch);
    debouncedCallApi();
  };

  const searchInput = (key) => (
    <Input
      onClick={(e) => e.stopPropagation()}
      value={search[key]}
      onChange={(e) => handleSearch(key, e.target.value)}
      placeholder=""
    />
  );

  const calculateDuration = (entry) => {
    if (entry.callAcceptedAt && entry.callEndedAt) {
      return moment(entry.callEndedAt).diff(
        moment(entry.callAcceptedAt),
        "seconds"
      );
    }
    return 0;
  };

  const filterData = () => {
    let filtered =
      callHistories?.map((entry) => ({
        ...entry,
        callDuration: calculateDuration(entry),
      })) || [];

    if (search.sender) {
      filtered = filtered.filter((entry) =>
        entry.sender?.name?.toLowerCase().includes(search.sender.toLowerCase())
      );
    }

    if (search.createdAt) {
      filtered = filtered.filter(
        (entry) =>
          entry.createdAt &&
          moment(entry.createdAt)
            .format("DD MMM YYYY hh:mm A")
            .toLowerCase()
            .includes(search.createdAt.toLowerCase())
      );
    }

    if (search.callAcceptedAt) {
      filtered = filtered.filter(
        (entry) =>
          entry.callAcceptedAt &&
          moment(entry.callAcceptedAt)
            .format("DD MMM YYYY hh:mm A")
            .toLowerCase()
            .includes(search.callAcceptedAt.toLowerCase())
      );
    }

    if (search.callEndedAt) {
      filtered = filtered.filter(
        (entry) =>
          entry.callEndedAt &&
          moment(entry.callEndedAt)
            .format("DD MMM YYYY hh:mm A")
            .toLowerCase()
            .includes(search.callEndedAt.toLowerCase())
      );
    }

    if (search.callRejectedAt) {
      filtered = filtered.filter(
        (entry) =>
          entry.callRejectedAt &&
          moment(entry.callRejectedAt)
            .format("DD MMM YYYY hh:mm A")
            .toLowerCase()
            .includes(search.callRejectedAt.toLowerCase())
      );
    }

    if (search.callDuration) {
      filtered = filtered.filter((entry) =>
        formatDuration(entry.callDuration)
          .toLowerCase()
          .includes(search.callDuration.toLowerCase())
      );
    }

    if (search.type) {
      filtered = filtered.filter((entry) =>
        entry.type?.toLowerCase().includes(search.type.toLowerCase())
      );
    }

    setDataSource(filtered);
  };

  const paginatedData = dataSource.slice(
    (page - 1) * resultPerPage,
    page * resultPerPage
  );

  const columns = [
    {
      title: () => (
        <div>
          <div className="special-table-header-text">Sender Name</div>
          {searchInput("sender")}
        </div>
      ),
      dataIndex: ["sender", "name"],
      key: "sender",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "sender" && sortedInfo?.order,
      render: (_, record) => record.sender?.name,
    },
    {
      title: () => (
        <div>
          <div className="special-table-header-text">Created At</div>
          {searchInput("createdAt")}
        </div>
      ),
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      width:"15%",
      sortOrder: sortedInfo?.columnKey === "createdAt" && sortedInfo?.order,
      render: (createdAt) =>
        createdAt ? moment(createdAt).format("DD MMM YYYY hh:mm A") : "",
    },
    {
      title: () => (
        <div>
          <div className="special-table-header-text">Accepted At</div>
          {searchInput("callAcceptedAt")}
        </div>
      ),
      dataIndex: "callAcceptedAt",
      key: "callAcceptedAt",
      sorter: true,
       width:"15%",
      sortOrder:
        sortedInfo?.columnKey === "callAcceptedAt" && sortedInfo?.order,
      render: (callAcceptedAt) =>
        callAcceptedAt
          ? moment(callAcceptedAt).format("DD MMM YYYY hh:mm A")
          : "-",
    },
    {
      title: () => (
        <div>
          <div className="special-table-header-text">Ended At</div>
          {searchInput("callEndedAt")}
        </div>
      ),
      dataIndex: "callEndedAt",
      key: "callEndedAt",
       width:"15%",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "callEndedAt" && sortedInfo?.order,
      render: (callEndedAt) =>
        callEndedAt ? moment(callEndedAt).format("DD MMM YYYY hh:mm A") : "-",
    },

    {
      title: () => (
        <div>
          <div className="special-table-header-text">Rejected At</div>
          {searchInput("callRejectedAt")}
        </div>
      ),
      dataIndex: "callRejectedAt",
      key: "callRejectedAt",
      sorter: true,
       width:"15%",
      sortOrder:
        sortedInfo?.columnKey === "callRejectedAt" && sortedInfo?.order,
      render: (rejectedAt) =>
        rejectedAt ? moment(rejectedAt).format("DD MMM YYYY hh:mm A") : "-",
    },
    {
      title: () => (
        <div>
          <div className="special-table-header-text">Duration</div>
          {searchInput("callDuration")}
        </div>
      ),
      dataIndex: "callDuration",
      key: "callDuration",
      sorter: true,
      align: "center",
      sortOrder: sortedInfo?.columnKey === "callDuration" && sortedInfo?.order,
      render: (d) =>
        d ? `${Math.floor(d / 60)}m ${Math.floor(d % 60)}s` : "-",
    },
    {
      title: () => (
        <div>
          <div className="special-table-header-text">Call Type</div>
          {searchInput("type")}
        </div>
      ),
      dataIndex: "type",
      key: "type",
      sorter: true,
      align: "center",
      sortOrder: sortedInfo?.columnKey === "type" && sortedInfo?.order,
      render: (type) => type || "",
    },
  ];

  return (
    <div className="page-top-div">
      <span className="text-span">Call History</span>
      <Button
        style={{ position: "absolute", right: "15mm", marginBottom: 20 }}
        onClick={() => navigate(-1)}
        icon={<BackIcon width={24} height={14} />}
      >
        Back
      </Button>

      <Row justify="center" className="production-manager-parent-row">
        <Col xxl={24} xl={24} lg={24} md={24} className="heading-col">
          <Row align="middle" gutter={[50, 0]}>
            <Col>
              <span className="heading-span">Call History</span>
            </Col>
          </Row>
        </Col>
        <Col lg={24} xl={24} xxl={24} md={24} style={{ minHeight: "70vh" }}>
          <Form component={false} className="table-style">
            <Spin spinning={fetchCallHistoriesLoading}>
              <Table
                className="special-table activity-type-table project-statuses-table"
                rowClassName="editable-row"
                dataSource={paginatedData}
                columns={columns}
                scroll={{ x: "100%" }}
                pagination={false}
                rowKey="_id"
                onChange={handleChange}
              />
            </Spin>
          </Form>
          <CustomPagination
            page={page}
            setPage={setPage}
            resultPerPage={resultPerPage}
            setResultPerPage={setResultPerPage}
            debouncedCallApi={debouncedCallApi}
            totalRecord={dataSource.length}
          />
        </Col>
      </Row>
    </div>
  );
};

export default UserCallHistory;
